import { createContext, useState, useContext, useEffect, useCallback } from "react"
import { jwtDecode } from "jwt-decode"
import api from "../services/api"
import API_ENDPOINTS from "../config/api"

const AuthContext = createContext(null)

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [clientInfo, setClientInfo] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)

  const setUserFromToken = useCallback(async (decodedToken) => {
    const { userId, sub } = decodedToken
    let { clientId, name, role, permissions } = sub

    // Get client info first to check expiration
    try {
      if (typeof clientId !== "string") clientId = clientId.id;
      const response = await api.get(API_ENDPOINTS.CLIENT.GET_ME)
      const clientData = response.data.client
      
      // Only check expiration if expiresAt is a valid date
      if (clientData.expiresAt && clientData.expiresAt !== null && new Date(clientData.expiresAt) < new Date()) {
        throw new Error("Your client subscription has expired. Please contact our customer service.")
      }

      setClientInfo(clientData)
      localStorage.setItem("clientInfo", JSON.stringify(clientData))
    } catch (err) {
      // throw err;
      console.log(err);
    }

    // Transform permissions array into an object for easier access
    const permissionsObj = permissions.reduce((acc, curr) => {
      const feature = Object.keys(curr)[0]
      const value = curr[feature]
      return { ...acc, [feature]: value }
    }, {})

    setUser({
      userId,
      clientId,
      name,
      roleId: role.id,
      roleName: role.name,
      permissions: permissionsObj,
    })
  }, [])

  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem("access_token")
      const storedClientInfo = localStorage.getItem("clientInfo")
      if (token) {
        try {
          const decodedToken = jwtDecode(token)
          await setUserFromToken(decodedToken)
          if (storedClientInfo) {
            setClientInfo(JSON.parse(storedClientInfo))
          }
        } catch (err) {
          console.error("Error initializing auth:", err)
          logout()
        }
      }
      setLoading(false)
    }
    initializeAuth()
  }, [setUserFromToken]) // Empty dependency array

  const login = async (credentials) => {
    try {
      const response = await api.post(API_ENDPOINTS.AUTH.LOGIN, credentials)
      if (response.status === 200) {
        const { access_token, refresh_token } = response.data
        const decodedToken = jwtDecode(access_token)
        
        try {
          // Only set tokens if client is not expired
          localStorage.setItem("access_token", access_token)
          localStorage.setItem("refresh_token", refresh_token)

          await setUserFromToken(decodedToken)
          setError(null)
          return true
        } catch (err) {
          setError(err.message || "Error setting user information")
          return false
        }
      } else {
        setError("Invalid email or password.")
        return false
      }
    } catch (err) {
      setError("Invalid email or password.")
      return false
    }
  }

  const logout = () => {
    localStorage.removeItem("access_token")
    localStorage.removeItem("refresh_token")
    localStorage.removeItem("clientInfo")
    setUser(null)
    setClientInfo(null)
  }

  const isAuthenticated = () => {
    const token = localStorage.getItem("access_token")
    const refreshToken = localStorage.getItem("refresh_token")
    
    if (!token || !refreshToken) {
      return false
    }
    
    try {
      const decodedToken = jwtDecode(token)
      const currentTime = Date.now() / 1000
      return decodedToken.exp > currentTime
    } catch (err) {
      console.error("Error checking authentication status:", err)
      return false
    }
  }

  return (
    <AuthContext.Provider value={{ user, clientInfo, login, logout, error, setError, isAuthenticated, loading }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
